import { useFieldArray, useForm } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Input } from "../../../app/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../app/components/ui/form";
import { Separator } from "../../../app/components/ui/separator";
import { ProductSelector } from "../../../components/selectors/ProductSelector";
import { DealerPickerPanel } from "../../dealers/components/DealerPickerPanel";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { applyLaravelValidationErrors, getErrorMessage, getLaravelValidationErrors } from "../../../lib/laravelErrors";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";
import { useState } from "react";

export type PurchaseOrderFormValues = {
  dealer_id: string;
  expected_delivery_date: string;
  notes: string;
  items: Array<{
    product_id: string;
    ordered_qty: string;
    purchase_price: string;
  }>;
};

const emptyItem = (): PurchaseOrderFormValues["items"][number] => ({
  product_id: "",
  ordered_qty: "1",
  purchase_price: "0",
});

type Props = {
  mode: "create" | "edit";
  purchaseOrderId?: string;
  initialValues?: Partial<PurchaseOrderFormValues>;
  onSuccess: () => void;
};

export function PurchaseOrderForm({ mode, purchaseOrderId, initialValues, onSuccess }: Props) {
  const { token } = useAuth();
  const [rootError, setRootError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<PurchaseOrderFormValues>({
    defaultValues: {
      dealer_id: initialValues?.dealer_id ?? "",
      expected_delivery_date: initialValues?.expected_delivery_date ?? "",
      notes: initialValues?.notes ?? "",
      items: initialValues?.items?.length ? initialValues.items : [emptyItem()],
    },
  });

  const items = useFieldArray({ control: form.control, name: "items" });
  const watchedItems = form.watch("items");

  async function onSubmit(values: PurchaseOrderFormValues) {
    if (!token) return;
    setRootError(null);
    setSubmitting(true);
    try {
      const body = {
        dealer_id: values.dealer_id,
        expected_delivery_date: values.expected_delivery_date?.trim() ? values.expected_delivery_date : null,
        notes: values.notes?.trim() ? values.notes : null,
        items: values.items.map((it) => ({
          product_id: it.product_id,
          ordered_qty: it.ordered_qty,
          purchase_price: it.purchase_price,
        })),
      };

      if (mode === "create") {
        await api.purchaseOrders.createPO({ token, payload: body });
        trackEvent(TRACKING_EVENTS.PO_CREATED);
      } else {
        if (!purchaseOrderId) throw new Error("Missing purchase order id");
        await api.purchaseOrders.updatePO({
          token,
          id: purchaseOrderId,
          payload: body,
        });
      }
      onSuccess();
    } catch (e) {
      const errors = getLaravelValidationErrors(e);
      if (errors) {
        applyLaravelValidationErrors(form.setError, errors);
        setRootError("Please fix the highlighted fields.");
        return;
      }
      setRootError(getErrorMessage(e, "Failed to save purchase order"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>Save failed</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="dealer_id"
              rules={{ required: "Dealer is required" }}
              render={({ field }) => (
                <FormItem className="md:col-span-3">
                  <FormLabel>Dealer</FormLabel>
                  <FormControl>
                    <DealerPickerPanel
                      value={field.value}
                      onChange={field.onChange}
                      items={(watchedItems ?? []).map((it) => ({ product_id: it.product_id, quantity: it.ordered_qty }))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expected_delivery_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expected delivery</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem className="md:col-span-3">
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Line items</div>
                <div className="text-sm text-muted-foreground">Products, quantities, and purchase price</div>
              </div>
              <Button type="button" variant="outline" onClick={() => items.append(emptyItem())}>
                Add row
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              {items.fields.map((f, idx) => (
                <div key={f.id} className="rounded-md border p-4 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">Row {idx + 1}</div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={items.fields.length <= 1}
                      onClick={() => items.remove(idx)}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name={`items.${idx}.product_id`}
                      rules={{ required: "Product is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Product</FormLabel>
                          <FormControl>
                            <ProductSelector value={field.value} onValueChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${idx}.ordered_qty`}
                      rules={{ required: "Ordered qty is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ordered qty</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${idx}.purchase_price`}
                      rules={{ required: "Purchase price is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Purchase price</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : mode === "create" ? "Create PO" : "Save changes"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
